/**
 * One gate, and every platform call goes through it. ADR-0006, FND-004 (`FIX`,
 * structural).
 *
 * The old app guards each platform call by hand — `?.` plus `try`/`catch`, with the Bot
 * API version that would fail named in a comment beside it
 * (`legacy/frontend/src/telegram.ts:66-115`). The degradations are right; there is just
 * nowhere for the next call to be added safely. This file is that place.
 *
 * **The behavior is identical to the old app's, by construction:**
 * - a method the client does not have is not called, and nothing throws;
 * - a client that throws instead of ignoring the call is caught;
 * - the app carries on in the state it would have been in anyway.
 *
 * The version is checked only when the client reports one. A client that reports
 * nothing is treated exactly as the old app treated it: the call is attempted, and its
 * absence or its refusal is caught. Refusing to try would be a behavioral change, and
 * a behavioral change here would be a new finding, not a tidy-up.
 */

import { webApp } from './bridge';

/** `6.1` < `7.7` < `8.0`. Telegram versions are two numeric parts. */
export function atLeast(reported: string, required: string): boolean {
  const [rMajor = 0, rMinor = 0] = reported.split('.').map((part) => Number.parseInt(part, 10));
  const [nMajor = 0, nMinor = 0] = required.split('.').map((part) => Number.parseInt(part, 10));
  if (Number.isNaN(rMajor) || Number.isNaN(rMinor)) return false;
  if (rMajor !== nMajor) return rMajor > nMajor;
  return rMinor >= nMinor;
}

export type Capability = {
  /** For the log and for the tests: which call this is. */
  readonly name: string;
  /** The Bot API version that introduced it, as Telegram documents it. */
  readonly since: string;
};

/**
 * The whole list of platform calls this app makes, with the version each needs. Adding
 * a call means adding a line here; there is no other way in.
 */
export const CAPABILITIES = {
  /** `legacy/frontend/src/telegram.ts:85-96` — older than 8.0 throws rather than ignoring. */
  requestFullscreen: { name: 'requestFullscreen', since: '8.0' },
  /** `legacy/frontend/src/telegram.ts:98-118` — older than 8.0 warns and returns. */
  lockOrientation: { name: 'lockOrientation', since: '8.0' },
  /** `legacy/frontend/src/telegram.ts:66-74` — older than 7.7 warns and returns. */
  disableVerticalSwipes: { name: 'disableVerticalSwipes', since: '7.7' },
} as const satisfies Record<string, Capability>;

export type CapabilityName = keyof typeof CAPABILITIES;

/**
 * Is this call worth attempting? `true` when the bridge has the method and either the
 * client reports a new enough version or reports none at all.
 */
export function supports(capability: CapabilityName): boolean {
  const bridge = webApp();
  if (bridge === undefined) return false;
  if (typeof bridge[capability] !== 'function') return false;
  const reported = bridge.version;
  if (reported === undefined || reported === '') return true;
  return atLeast(reported, CAPABILITIES[capability].since);
}

/**
 * Attempt a platform call. Returns whether it went out — never throws, and never leaves
 * the app in a state the old one would not have been in.
 */
export function attempt(capability: CapabilityName): boolean {
  if (!supports(capability)) return false;
  try {
    webApp()?.[capability]?.();
    return true;
  } catch {
    // A client old enough to throw rather than ignore. The app keeps whatever it had:
    // Telegram's own header, the current orientation, the vertical swipe.
    return false;
  }
}
